import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Bot, Copy, RefreshCw, Send, CheckCircle2, AlertCircle, Webhook } from "lucide-react";

export default function AiIntegrationPanel() {
  const [settings, setSettings] = useState(null);
  const [events, setEvents] = useState([]);
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [s, e] = await Promise.all([api.get("/admin/webhooks"), api.get("/admin/events?limit=30")]);
      setSettings(s.data);
      setUrl(s.data.webhook_url || "");
      setSecret(s.data.secret || "");
      setEvents(e.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, []);

  const save = async () => {
    setBusy(true); setMsg(""); setError("");
    try {
      const { data } = await api.put("/admin/webhooks", { webhook_url: url, secret, enabled: true });
      setSettings(data); setMsg("Saved.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const regen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/webhooks/regenerate-key");
      setSettings({ ...settings, api_key: data.api_key });
      setMsg("API key regenerated.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const fireTest = async () => {
    setBusy(true);
    try {
      await api.post("/admin/webhooks/test");
      setMsg("Test event fired.");
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(settings?.api_key || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="ai-panel">
      <div className="bg-asap-deep text-white border-2 border-asap-deep p-6">
        <div className="flex items-center gap-3">
          <Bot size={28} className="text-asap-yellow"/>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-asap-yellow font-bold">AI Monitoring</div>
            <div className="font-display text-2xl uppercase">Connect your AI app</div>
          </div>
        </div>
        <p className="text-white/70 mt-3 text-sm max-w-2xl">Send live platform events (new bookings, dispatch, payments, SLA breaches) to your AI app via outbound webhook, and let it pull data on-demand using the API key below.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border-2 border-asap-deep p-5">
          <h3 className="font-display uppercase text-asap-deep mb-2 flex items-center gap-2"><Webhook size={18}/> Outbound Webhook</h3>
          <p className="text-xs text-slate-500 mb-4">We'll POST every event to this URL. Paste your AI app's webhook endpoint (any URL inside Emergent or external).</p>
          <Field label="Webhook URL">
            <input data-testid="webhook-url" value={url} onChange={(e) => setUrl(e.target.value)} className="w-full px-4 py-2 border-2 border-asap-deep focus:border-asap-orange outline-none" placeholder="https://your-ai-app.preview.emergentagent.com/api/asap-events" />
          </Field>
          <Field label="Shared Secret (optional, sent as X-ASAP-Secret header)">
            <input data-testid="webhook-secret" value={secret} onChange={(e) => setSecret(e.target.value)} className="w-full px-4 py-2 border-2 border-asap-deep focus:border-asap-orange outline-none" placeholder="any-secret-string" />
          </Field>
          <div className="flex flex-wrap gap-3 mt-4">
            <button data-testid="webhook-save" disabled={busy} onClick={save} className="px-4 py-2 bg-asap-yellow text-asap-deep font-display uppercase border-2 border-asap-deep shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60">Save</button>
            <button data-testid="webhook-test" disabled={busy || !settings?.webhook_url} onClick={fireTest} className="px-4 py-2 border-2 border-asap-deep text-asap-deep font-bold uppercase hover:bg-asap-deep hover:text-white transition-all inline-flex items-center gap-2 disabled:opacity-50"><Send size={14}/> Fire test event</button>
          </div>
        </div>

        <div className="bg-white border-2 border-asap-deep p-5">
          <h3 className="font-display uppercase text-asap-deep mb-2 flex items-center gap-2"><Bot size={18}/> AI Read API Key</h3>
          <p className="text-xs text-slate-500 mb-4">Use this key from your AI app to call <code className="bg-slate-100 px-1">GET /api/ai/feed</code> and <code className="bg-slate-100 px-1">GET /api/ai/summary</code>. Send as header <code className="bg-slate-100 px-1">X-API-Key</code>.</p>
          <div className="bg-asap-deep text-asap-yellow font-mono text-xs p-3 break-all border-2 border-asap-deep" data-testid="ai-api-key">{settings?.api_key || "—"}</div>
          <div className="flex gap-3 mt-3">
            <button data-testid="copy-key" onClick={copyKey} className="px-3 py-2 border-2 border-asap-deep text-asap-deep font-bold uppercase text-xs hover:bg-asap-deep hover:text-white inline-flex items-center gap-2"><Copy size={14}/> {copied ? "Copied!" : "Copy"}</button>
            <button data-testid="regen-key" disabled={busy} onClick={regen} className="px-3 py-2 border-2 border-asap-red text-asap-red font-bold uppercase text-xs hover:bg-asap-red hover:text-white inline-flex items-center gap-2 disabled:opacity-50"><RefreshCw size={14}/> Regenerate</button>
          </div>

          <div className="mt-5 text-xs space-y-1 bg-slate-50 p-3 rounded border border-slate-200">
            <div className="font-bold uppercase tracking-widest text-asap-deep">Quick start (curl)</div>
            <pre className="overflow-x-auto text-[11px] text-slate-700 mt-1">{`curl -H "X-API-Key: ${settings?.api_key?.slice(0,8) || "..."}…" \\
  ${window.location.origin}/api/ai/feed?limit=20`}</pre>
          </div>
        </div>
      </div>

      {(msg || error) && (
        <div className={`p-3 border-2 font-bold text-sm inline-flex items-center gap-2 ${error ? "border-asap-red text-asap-red bg-red-50" : "border-emerald-500 text-emerald-700 bg-emerald-50"}`} data-testid="ai-msg">
          {error ? <AlertCircle size={16}/> : <CheckCircle2 size={16}/>}
          {error || msg}
        </div>
      )}

      <div className="bg-white border-2 border-asap-deep">
        <div className="p-5 border-b-2 border-asap-deep flex items-center justify-between">
          <h3 className="font-display uppercase text-asap-deep">Recent Events ({events.length})</h3>
          <span className="text-[10px] uppercase tracking-widest text-slate-500">Auto-refreshes every 6s</span>
        </div>
        <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-100" data-testid="events-list">
          {events.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No events yet — create a booking or fire a test.</div>}
          {events.map((e) => (
            <div key={e.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`event-${e.id}`}>
              <span className={`text-[10px] px-2 py-0.5 font-bold uppercase tracking-widest border ${badgeFor(e.type)}`}>{e.type}</span>
              <span className="text-slate-500 text-xs">{new Date(e.created_at).toLocaleString()}</span>
              <span className="text-slate-700 truncate flex-1">{JSON.stringify(e.payload)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-asap-deep mb-1">{label}</div>
      {children}
    </label>
  );
}

function badgeFor(t) {
  if (t.startsWith("booking.created")) return "border-asap-orange text-asap-orange";
  if (t.startsWith("booking.accepted")) return "border-asap-blue text-asap-blue";
  if (t.startsWith("booking.completed")) return "border-emerald-500 text-emerald-700";
  if (t.startsWith("payment")) return "border-yellow-600 text-yellow-700 bg-yellow-50";
  return "border-slate-400 text-slate-600";
}
